import DSAuth from '../DSAuth';
import { AuthChoice } from '../data/AuthChoice';
import { GenericRequest } from '../data/AuthTypes';
import AuthUtils from '../../AuthUtils';
import { HTTPFetchResponse } from '../data/AuthTypes';
import { Logger } from '../../Logger';
import { logConstants } from '../../../utils/Constants';
import { PrimaryTokenFetchFailure } from '../../AuthErrors';
import { headerConstants, gatewayConstants, stringConstants } from '../../AuthConstants';
import { HttpStatusCodes } from '../../../utils/http/HttpConstants';
import GatewayService from '../../gateway/GatewayService';
import {
	Authenticator,
	analyticsConstants,
	isTokenResponse,
	logEvent,
} from './Authenticator';
import { Protocol } from '../../AuthConfig';
import { HeaderParser } from '../parser/HeaderParser';
import { StoreType } from '../../../Common/TypeDefinitions';

export default class GatewayAuthenticator implements Authenticator {
	private protocol: string;

	async getToken(
		authProtocol: AuthChoice,
		authProtocolsRequest: GenericRequest,
		id: string,
		referenceUri: string,
		transactionId: string
	): Promise<HTTPFetchResponse> {
		this.protocol = authProtocol.getProtocol();
		const response = await this.fetchPrimaryToken(
			authProtocol.getLocation(),
			authProtocolsRequest,
			id,
			referenceUri,
			transactionId
		);

		this.checkSsoFailure(response);
		return response;
	}

	private async fetchPrimaryToken(
		authProtocolLocation: string,
		authProtocolsRequest: GenericRequest,
		id: string,
		referenceUri: string,
		transactionId: string
	): Promise<HTTPFetchResponse> {
		try {
			const headers = DSAuth.getInstance().addBasicHeaders(
				AuthUtils.getInstance().addLabelAndCredTypesToRequestHeader(
					{
						Accept: headerConstants.TOKEN_RESPONSE_CONTENT_TYPE,
						[headerConstants.CONTENT_TYPE]: headerConstants.TOKEN_REQUEST_CONTENT_TYPE,
						'Cache-Control': 'no-cache',
						[headerConstants.ACCEPT_LANGUAGE]: 'en-US', // TODO: use locale
					},
					true
				),
				transactionId
			);

			headers['Encode'] = 'False';
			const response = await GatewayService.getInstance().fetch(
				stringConstants.POST,
				authProtocolLocation,
				headers,
				authProtocolsRequest.body,
				referenceUri,
				true,
				transactionId,
				''
			);

			if (isTokenResponse(response)) {
				this.logEvent(referenceUri);
			}
			return response;
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				`${id}: PrimaryTokenFetchFailure for gateway`
			);
			throw new PrimaryTokenFetchFailure(e);
		}
	}

	private checkSsoFailure(response: HTTPFetchResponse) {
		if (
			this.protocol === Protocol.CitrixAGBasicNoPassword &&
			response.statusCode === HttpStatusCodes.HTTP_STATUS_FORBIDDEN &&
			HeaderParser.getInstance().extractHeaderValue(
				response.headers,
				gatewayConstants.SSO_FAILURE_REASON_HEADER
			) === gatewayConstants.SSO_FAILURE_BAD_CREDS
		) {
			Logger.getInstance().write(
				logConstants.INFO,
				`GatewayAuthenticator: This failure typically occurs with certificate based authentication when \
the StoreFront authentication service rejects the user name which Access Gateway extracted from the \
client certificate. Check that the User Name Field of the Access Gateway authentication server/request profile \
is set to 'SubjectAltName:PrincipalName'.`
			);
		}
	}

	private logEvent(referenceUri: string) {
		const serverType = GatewayService.getInstance().getGatewaySessionType(referenceUri);
		if (this.protocol.toLowerCase() === Protocol.CitrixAGBasicNoPassword.toLowerCase()) {
			if (serverType === StoreType.AdvancedGateway) {
				logEvent(analyticsConstants.LABEL_AGSSO_ADVNOPWD_AUTHENTICATOR, 1);
			} else if (serverType === StoreType.BasicGateway) {
				logEvent(analyticsConstants.LABEL_AGSSO_BASICNOPWD_AUTHENTICATOR, 1);
			}
		} else if (this.protocol.toLowerCase() === Protocol.CitrixAGBasic.toLowerCase()) {
			if (serverType === StoreType.AdvancedGateway) {
				logEvent(analyticsConstants.LABEL_AGSSO_ADV_AUTHENTICATOR, 1);
			} else if (serverType === StoreType.BasicGateway) {
				logEvent(analyticsConstants.LABEL_AGSSO_BASIC_AUTHENTICATOR, 1);
			}
		}
	}
}
